import { Timer } from "./Timer";
import { application } from "./WebApp.Instance";

type PopoverElement = HTMLElement & { showPopover(options?: { source: HTMLElement }): void };

interface PopoverToggleEvent extends Event {
  newState: string;
}

export class PopUp {
  element: HTMLElement;
  parent: HTMLElement;
  anchorElement: HTMLElement;
  popup: PopoverElement | null;
  restore: boolean;
  trigger: HTMLElement | null;
  delay: number;
  openTimer: any;
  closeTimer: any;
  anchorObserver: IntersectionObserver | null;

  constructor(
    element: HTMLElement,
    parent: HTMLElement,
    restore: boolean,
    trigger: HTMLElement | null = null,
    delay: number = 0,
    anchorElement: HTMLElement | null = null
  ) {
    this.element = element;
    this.parent = parent;
    this.anchorElement = anchorElement ?? parent;
    this.popup = null;
    this.restore = restore;
    this.trigger = trigger;
    this.delay = delay;
    this.openTimer = new Timer(delay);
    this.closeTimer = new Timer(delay);
    this.anchorObserver = null;
  }

  open(anchorLeft: number | null = null): void {
    this.closeTimer.kill();

    if (this.delay > 0) {
      this.openTimer.kill();
      this.openTimer.schedule(() => { this.show(anchorLeft); });
    }
    else
      this.show(anchorLeft);
  }

  close(): void {
    this.openTimer.kill();

    if (this.delay > 0) {
      this.closeTimer.kill();
      this.closeTimer.schedule(() => { this.hide(); });
    }
    else
      this.hide();
  }

  show(anchorLeft: number | null = null): void {
    if (!this.isOpen()) {
      const anchorName = `--pop-up-${crypto.randomUUID()}`;

      this.popup = document.createElement("div") as PopoverElement;
      this.popup.classList.add("PopUp");
      this.popup.appendChild(this.element);
      this.popup.style.setProperty("position-anchor", anchorName);

      this.anchorElement.style.setProperty("anchor-name", anchorName);

      if (anchorLeft !== null) {
        const offset = anchorLeft - this.anchorElement.getBoundingClientRect().left;

        this.popup.classList.add("AnchoredToPointer");
        this.popup.style.setProperty("--pop-up-anchor-offset", offset + "px");
      }

      this.getContainer().appendChild(this.popup);
      this.popup.setAttribute("popover", "auto");

      this.popup.addEventListener(
        "toggle",
        (event: Event) => {
          if ((event as PopoverToggleEvent).newState === "closed")
            this.hide();
        }
      );

      if (this.delay > 0) {
        this.popup.addEventListener("mouseenter", (event) => { this.closeTimer.kill(); });
        this.popup.addEventListener("mouseleave", (event) => { this.close(); });
      }

      this.observeAnchor();

      if (this.trigger !== null)
        this.popup.showPopover({ source: this.trigger });
      else
        this.popup.showPopover();
    }
  }

  hide(): void {
    if (this.isOpen()) {
      this.anchorObserver?.disconnect();
      this.anchorObserver = null;

      this.anchorElement.style.removeProperty("anchor-name");

      if (this.restore)
        this.parent.appendChild(this.element);

      this.getContainer().removeChild(this.popup);
      this.popup = null;
    }
  }

  observeAnchor(): void {
    this.anchorObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        const latestEntry = entries[entries.length - 1];

        if (!latestEntry.isIntersecting)
          this.close();
      }
    );

    this.anchorObserver.observe(this.anchorElement);
  }

  getContainer(): HTMLElement {
    return application.pageHandler.contents.element;
  }

  isOpen(): boolean {
    return this.popup !== null;
  }
}
